<template>
    <a :id="name ? '#discountBanner' : ''" :href="link ?? '/produkte'">
        <Notification v-if="(descriptionBefore || descriptionAfter) && showBanner" class="text-white border-b border-gray-300 whitespace-nowrap" style="background: #353cee">
            <div class="flex flex-col xl:flex-row justify-center w-full items-center">
                <p class="leading-4 block text-xs xl:text-base" v-html="descriptionBefore"></p>
                <div class="flex items-center md:flex-row">
                    <button
                        v-if="discount_code"
                        aria-label="Rabattcode kopieren"
                        class="m-1 p-1 rounded-md shadow border border-amber-400 text-xs xl:text-base flex items-center gap-1.5 cursor-pointer hover:bg-white/10 transition select-text"
                        @click.prevent="copyCode"
                    >
                        {{ $t('discount_code') }} {{ discount_code }}
                        <span v-if="copied" class="text-green-400 font-semibold">✓</span>
                        <svg v-else xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5 text-amber-300 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                    </button>
                    <p v-if="showCountdown" aria-label="Countdown" :class="['leading-4 px-2 flex gap-1', mobilePromotionBanner && 'pb-1 ']">
                        <span :class="[mobilePromotionBanner && 'text-xs']">{{ currentCountdown }}</span>
                    </p>
                </div>
                <p aria-label="Rabattcode-Message" :class="['text-xs xl:text-base xl:block leading-4']" v-html="descriptionAfter"></p>
            </div>
        </Notification>
    </a>

    <!-- Marketing Popup -->
    <teleport to="body">
        <template v-if="marketingModalOpen && showBanner && banner_image">
            <Popup :overflow-hidden="true" :open="marketingModalOpen" class="px-6" @open="closeAction">
                <div class="cursor-pointer" @click="marketingPopupClick()">
                    <video v-if="isWebm(banner_image)" class="block w-full h-fullxw" autoplay loop muted="" playsinline>
                        <source :src="banner_image" type="video/webm" />
                    </video>
                    <img v-else-if="isImage(banner_image)" :alt="banner_image_alt === '' ? 'Rabattaktion' : banner_image_alt" class="block w-full h-full" :src="banner_image" />
                </div>
            </Popup>
        </template>
    </teleport>
    <!-- End Marketing Popup -->
</template>

<script>
import i18next from 'i18next';

export default {
    data: () => ({
        copied: false,
        discount_code: '',
        startBanner: '',
        startCountdown: '',
        endTimeDate: '',
        descriptionBefore: '',
        descriptionAfter: '',
        banner_image: '',
        banner_image_alt: '',
        showBanner: false,
        showCountdown: false,
        mobilePromotionBanner: false,
        bannerDay: {
            de: 'Tag',
            ch: 'Day',
            plural: {
                de: 'Tage',
                ch: 'Days'
            }
        },
        bannerHours: {
            de: 'Stunde',
            ch: 'Hour',
            plural: {
                de: 'Stunden',
                ch: 'Hours'
            }
        },
        bannerMinutes: {
            de: 'Minute',
            ch: 'Minute',
            plural: {
                de: 'Minuten',
                ch: 'Minutes'
            }
        },
        bannerSeconds: {
            de: 'Sekunde',
            ch: 'Second',
            plural: {
                de: 'Sekunden',
                ch: 'Seconds'
            }
        },
        mobileBannerDay: {
            de: 'T',
            ch: 'D',
            plural: {
                de: 'T',
                ch: 'D'
            }
        },
        mobileBannerHours: {
            de: 'St',
            ch: 'H',
            plural: {
                de: 'St',
                ch: 'H'
            }
        },
        mobileBannerMinutes: {
            de: 'Min',
            ch: 'M',
            plural: {
                de: 'Min',
                ch: 'M'
            }
        },
        mobileBannerSeconds: {
            de: 'Sek',
            ch: 'S',
            plural: {
                de: 'Sek',
                ch: 'S'
            }
        },
        bannerDayLabel: null,
        bannerHoursLabel: null,
        bannerMinutesLabel: null,
        bannerSecondsLabel: null,
        bannerDiscountName: '',
        marketingModalOpen: false,
        showMarketingBanner: true,
        currentCountdown: null,
        link: '',
        name: '',
        region: window.meta?.region_configuration.region ?? 'de'
    }),
    mounted() {
        if (window.meta?.discount_promotion) {
            const discountPromotion = window.meta.discount_promotion;

            this.name = discountPromotion.name;
            this.discount_code = discountPromotion.discount_code;
            this.startBanner = new Date(discountPromotion.start_banner_date);
            this.startCountdown = new Date(discountPromotion.start_countdown_date);
            this.endTimeDate = new Date(discountPromotion.end_time_date);
            this.descriptionBefore = discountPromotion.banner_text_left;
            this.descriptionAfter = discountPromotion.banner_text_right;
            this.banner_image = discountPromotion.banner_image;
            this.banner_image_alt = discountPromotion.banner_image_alt;
            this.link = discountPromotion.link;
            this.countdown();
        }

        this.setMobilePromotionBanner();

        window.addEventListener('resize', () => {
            this.setMobilePromotionBanner();
        });

        // marketing modal
        setTimeout(() => {
            if (this.$route.fullPath === '/') {
                this.marketingModalOpen = true;
            }
        }, 1000);
    },
    methods: {
        isWebm(file) {
            return typeof file === 'string' && file.trim().toLowerCase().endsWith('.webm');
        },
        isImage(file) {
            const exts = ['.jpg', '.jpeg', '.png'];

            return typeof file === 'string' && exts.some((ext) => file.trim().toLowerCase().endsWith(ext));
        },
        countdown() {
            const zeroPad = (num, places) => String(num).padStart(places, '0');
            const interval = 1000;
            const startBanner = this.$moment(this.startBanner);
            const startCountdown = this.$moment(this.startCountdown); // 36h vor Ende der Aktion
            const endTime = this.$moment(this.endTimeDate);
            const currentTime = this.$moment();
            let duration = this.$moment.duration(endTime.diff(currentTime));

            setInterval(() => {
                duration = this.$moment.duration(duration - interval, 'milliseconds');

                if (duration.asSeconds() <= 0) {
                    this.currentCountdown = null;
                } else {
                    const dayCount = duration.days();
                    const hourCount = duration.hours();
                    const minuteCount = duration.minutes();
                    const secondCount = duration.seconds();

                    const pluralize = (count, labelObj, region) => {
                        const isPlural = count !== 1;

                        return isPlural ? labelObj.plural[region] : labelObj[region];
                    };

                    const daysLabel = pluralize(dayCount, this.bannerDayLabel, this.region);
                    const hoursLabel = pluralize(hourCount, this.bannerHoursLabel, this.region);
                    const minutesLabel = pluralize(minuteCount, this.bannerMinutesLabel, this.region);
                    const secondsLabel = pluralize(secondCount, this.bannerSecondsLabel, this.region);

                    this.currentCountdown = `
                        ${dayCount} ${daysLabel}
                        ${hourCount} ${hoursLabel}
                        ${minuteCount} ${minutesLabel}
                        ${secondCount} ${secondsLabel}
                    `.trim();
                }
            }, interval);

            this.showBanner = !!(endTime.diff(this.$moment()) > 0 && startBanner.diff(this.$moment()) < 0);
            this.showCountdown = !!(endTime.diff(this.$moment()) > 0 && startCountdown.diff(this.$moment()) < 0);
        },
        marketingPopupClick() {
            this.marketingModalOpen = false;
            window.location.href = this.link ?? '/produkte';
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        },
        closeAction() {
            this.marketingModalOpen = false;
        },
        copyCode() {
            if (!this.discount_code) return;

            navigator.clipboard.writeText(this.discount_code).then(() => {
                this.copied = true;
                setTimeout(() => { this.copied = false; }, 2000);
            });
        },
        setMobilePromotionBanner() {
            if (window.innerWidth <= 900) {
                this.bannerDayLabel = this.mobileBannerDay;
                this.bannerHoursLabel = this.mobileBannerHours;
                this.bannerMinutesLabel = this.mobileBannerMinutes;
                this.bannerSecondsLabel = this.mobileBannerSeconds;

                this.mobilePromotionBanner = true;
            } else {
                this.bannerDayLabel = this.bannerDay;
                this.bannerHoursLabel = this.bannerHours;
                this.bannerMinutesLabel = this.bannerMinutes;
                this.bannerSecondsLabel = this.bannerSeconds;

                this.mobilePromotionBanner = false;
            }
        }
    }
};
</script>
